import { useCallback, useEffect, useState } from 'react';
import { verifyReceipt } from '~/api';
import { VerifyReceiptRequest, VerifyReceiptResponse } from '~/types/dart';
import { isInApp } from '~/utils';
import { airbridgeEvent, DartPurchaseEvent } from '~/utils/airbridge';

export type PaymentData = {
  id: string;
  count: number;
  price: number;
};

type PurchaseStatus = 'purchased' | 'restored' | 'error' | 'cancel' | 'pending';

type IOSSuccess = {
  status: 'purchased';
  purchaseDetails: string;
  source: 'app_store';
  isSandBox: boolean;
  error: null;
};

type AOSSuccess = {
  status: 'purchased';
  purchaseDetails: {
    purchaseID: string;
    productID: string;
    status: PurchaseStatus;
    verificationData: {
      localVerificationData: string | object; // iOS에서는 문자열, Android에서는 JSON 객체
      serverVerificationData: string;
      source: 'app_store' | 'google_play';
    };
    transactionDate: string;
    pendingCompletePurchase: boolean;
  };
  source: 'google_play';
  error: null;
};

type PurchaseSuccessResponse = IOSSuccess | AOSSuccess;

type PurchaseEtcResponse = {
  status: Omit<PurchaseStatus, 'purchased'>;
  purchaseDetails: null;
  source: 'app_store' | 'google_play';
  error: {
    source: string | null;
    code: string | null;
    message: string | null;
    details: any | null;
  } | null;
};

type PurchaseResponse = PurchaseSuccessResponse | PurchaseEtcResponse;

const useFlutterInAppWebView = (
  email: string,
  {
    onSuccess,
    onError,
    onCancel,
    onRestore,
    onPending,
  }: {
    onSuccess?: () => void;
    onError?: (errorMsg?: string) => void;
    onCancel?: () => void;
    onRestore?: () => void;
    onPending?: () => void;
  } = {},
) => {
  // 구매 증빙 검증 중
  const [isVerifying, setIsVerifying] = useState(false);
  const [productID, setProductID] = useState('');
  const [purchaseInfo, setPurchaseInfo] = useState<
    Omit<DartPurchaseEvent, 'status'>
  >({
    product_id: '',
    product_name: '',
    product_price: 0,
    product_quantity: 0,
    currency: 'USD',
    inapp_purchased: true,
  });
  const [isAppEnvironment, setIsAppEnvironment] = useState(true);

  /**
   * 케이스 구분 없이 앱단에서 내려오는 데이터 전부 전송
   */
  const handleVerifyReceipt = async (
    request: VerifyReceiptRequest,
    status: PurchaseStatus,
  ) => {
    if (status === 'purchased') {
      setIsVerifying(true);
    }

    try {
      const response: VerifyReceiptResponse = await verifyReceipt(request);
      // 구매 완료 케이스만
      if (status !== 'purchased' || response.result !== 1) return;
      const { is_valid } = response.data;
      if (!is_valid) {
        return onError?.('Failed to verify purchase. Please try again.');
      }

      onSuccess?.();

      // send airbridge event : orderCompleted
      airbridgeEvent.purchaseDart(email, {
        ...purchaseInfo,
        status: 'orderCompleted',
      });
    } catch (error) {
      onError?.('Failed to verify purchase. Please contact support.');
      console.error('Verification error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * 앱의 구매 상태 변화시 호출되는 함수
   * @param receiptJsonString 구매 증빙 데이터
   */
  const handlePurchaseStatusChange = (receiptJsonString: string) => {
    const { purchaseDetails, status, source, error } = JSON.parse(
      receiptJsonString,
    ) as PurchaseResponse;

    switch (status) {
      case 'purchased':
        if (!purchaseDetails || !source) {
          onError?.('Purchase verification failed due to incomplete data.');
        }
        break;
      case 'error':
        onError?.(error?.message || 'Error occurred.');
        break;
      case 'cancel':
        onCancel?.();
        break;
      case 'restored':
        onRestore?.();
        break;
      case 'pending':
        onPending?.();
        break;
    }

    // NOTE: 이제 앱에서 PENDING 시 호출 안 할 예정
    handleVerifyReceipt(
      {
        id: productID,
        email,
        platform: source,
        encrypted_receipt: receiptJsonString,
      },
      status as PurchaseStatus,
    );
  };

  const callFlutterHandler = useCallback(
    (paymentData: PaymentData) => {
      if (!window.flutter_inappwebview) return;

      const purchaseData = {
        product_id: paymentData.id,
        product_name: String(paymentData.count),
        product_quantity: paymentData.count,
        product_price: paymentData.price,
        currency: 'USD',
        inapp_purchased: isAppEnvironment,
      };
      setProductID(paymentData.id);
      setPurchaseInfo(purchaseData);

      window.flutter_inappwebview.callHandler('purchase', paymentData);

      // send airbridge event : initiateCheckout
      airbridgeEvent.purchaseDart(email, {
        ...purchaseData,
        status: 'initiateCheckout',
      });
    },
    [email, isAppEnvironment],
  );

  useEffect(() => {
    setIsAppEnvironment(isInApp());

    const handleFlutterInAppWebViewPlatformReady = () => {
      setIsAppEnvironment(isInApp());
    };

    window.addEventListener(
      'flutterInAppWebViewPlatformReady',
      handleFlutterInAppWebViewPlatformReady,
    );

    // 앱단에서 구매 상태 변화시 호출되는 함수
    window.purchasedApp = handlePurchaseStatusChange;

    return () => {
      window.removeEventListener(
        'flutterInAppWebViewPlatformReady',
        handleFlutterInAppWebViewPlatformReady,
      );

      delete window.purchasedApp;
    };
  }, [handleVerifyReceipt, email]);

  return {
    isVerifying,
    isAppEnvironment,
    callFlutterHandler,
  };
};

export default useFlutterInAppWebView;
